import React, { FC, useContext } from 'react'
import { useMsal } from '@azure/msal-react'
import { Icon } from '@components/ui/Icon'
import { logoutIcon } from '@images/icons'
import { makeClasses } from '@utils/styles'
import Link from '@components/ui/link'
import { AppStateContext } from '@components/layouts/DynamicLayout'
import config from '@utils/config'

type Props = {
	text: string
	className?: string
	hasIcon?: boolean
}

type ClassType = {
	wrapper: string
	icon: string
}

const classes: ClassType = makeClasses({
	wrapper: {
		marginRight: '10px'
	},
	icon: {
		width: '18px',
		verticalAlign: 'middle'
	}
})

export const SignOutButton: FC<Props> = ({ text, className, hasIcon }) => {
	const { instance } = useMsal()
	const { setAuthUser } = useContext(AppStateContext)

	const handleLogout = () => {
		// Clear the app-level user state and its associated localStorage cache.
		// Note: MSAL-managed keys (x-ms-cpim-sso, x-ms-cpim-csrf) are intentionally
		// NOT removed here – MSAL handles them internally during logoutRedirect.
		// Removing them manually before the redirect can corrupt the B2C logout
		// flow and cause intermittent failures where the user appears still logged in.
		setAuthUser(null)

		// Stamp the logout timestamp so RedirectComponent can detect an immediate
		// post-logout redirect and avoid auto-triggering a new sign-in.
		localStorage.setItem('logoutInProgressAt', String(Date.now()))

		// Clear the saved deep-link redirect so a future login starts from home.
		localStorage.removeItem(config.localStorage.authRedirect)

		// Clean localStorage related to REPERE synchronization
		localStorage.removeItem('lastSynchRepere')
		localStorage.removeItem('sasTokenData')

		// Resolve the active account explicitly so MSAL targets the correct
		// B2C session during the server-side logout call.
		const activeAccount = instance.getActiveAccount()

		if (activeAccount) {
			instance.logoutRedirect({
				account: activeAccount,
				postLogoutRedirectUri: '/'
			})
		} else {
			// Avoid falling back to the first cached account here, because that
			// account may be stale or belong to a different user.
			instance.logoutRedirect({
				postLogoutRedirectUri: '/'
			})
		}
	}

	return (
		<Link
			to="#"
			className={className}
			onClick={handleLogout}
			key="logoutRedirect"
			data-tracking-type="cta"
		>
			{hasIcon && (
				<div className={classes.wrapper}>
					<Icon src={logoutIcon} className={classes.icon} />
				</div>
			)}{' '}
			{text}
		</Link>
	)
}
